import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { prefetchRoute } from "../lib/queries";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  CalendarDays,
  Calendar,
  CheckSquare,
  Camera,
  Users,
  Building2,
  UserPlus,
  Settings,
  CircleMinus,
  Plus,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";

// One navigation row: a thin icon, the label and an optional "+" quick action.
const NavItem = ({ item, onClose }) => {
  const [name, Icon, path, testId, quick] = item;
  return (
    <div className="nav-row">
      <NavLink
        to={path}
        onClick={onClose}
        onMouseEnter={() => prefetchRoute(path)}
        onFocus={() => prefetchRoute(path)}
        data-testid={testId}
        className={({ isActive }) => `sidebar-link ${isActive ? "is-active" : ""}`}
      >
        <span className="nav-icon">
          <Icon size={18} strokeWidth={1.6} />
        </span>
        <span className="nav-label">{name}</span>
      </NavLink>
      {quick && (
        <Link
          to={quick.to}
          onClick={onClose}
          className="nav-quick"
          aria-label={quick.label}
          title={quick.label}
          data-testid={`${testId}-quick`}
        >
          <Plus size={13} strokeWidth={2} />
        </Link>
      )}
    </div>
  );
};

const SidebarContent = ({ onClose }) => {
  const { logout, isAdmin, isMediaHead } = useAuth();
  const navigate = useNavigate();
  const workspace = isMediaHead
    ? [
        ["Overview", LayoutDashboard, "/dashboard", "nav-dashboard"],
        [
          "Events",
          CalendarDays,
          "/events",
          "nav-events",
          { to: "/events?create=new", label: "Create event" },
        ],
        [
          "Tasks",
          CheckSquare,
          "/tasks",
          "nav-tasks",
          { to: "/tasks?create=new", label: "Create task" },
        ],
        ["Calendar", Calendar, "/calendar", "nav-calendar"],
        ["Employees", Users, "/employees", "nav-employees"],
      ]
    : [
        ["My tasks", CheckSquare, "/my-tasks", "nav-my-tasks"],
        ["My calendar", Calendar, "/my-calendar", "nav-my-calendar"],
      ];
  const management = isAdmin
    ? [
        ["Institutions", Building2, "/institutions", "nav-institutions"],
        ["Equipment", Camera, "/equipment", "nav-equipment"],
        ["Users", Users, "/users", "nav-users"],
        ["Create user", UserPlus, "/create-user", "nav-create-user"],
      ]
    : [];
  return (
    <div className="sidebar-content">
      <nav aria-label="Main navigation" className="sidebar-nav">
        <div className="nav-section">
          <p className="sr-only">Workspace</p>
          {workspace.map((item) => (
            <NavItem key={item[2]} item={item} onClose={onClose} />
          ))}
        </div>
        {management.length > 0 && (
          <div className="nav-section nav-section-divided">
            <p className="sr-only">Management</p>
            {management.map((item) => (
              <NavItem key={item[2]} item={item} onClose={onClose} />
            ))}
          </div>
        )}
      </nav>
      <div className="sidebar-bottom">
        <NavLink
          to="/change-password"
          onClick={onClose}
          className={({ isActive }) => `sidebar-link ${isActive ? "is-active" : ""}`}
          data-testid="nav-change-password"
        >
          <span className="nav-icon">
            <Settings size={18} strokeWidth={1.6} />
          </span>
          <span className="nav-label">Account settings</span>
        </NavLink>
        <button
          className="sidebar-link logout-link"
          data-testid="sidebar-logout-button"
          onClick={() => {
            logout();
            navigate("/login");
          }}
        >
          <span className="nav-icon">
            <CircleMinus size={18} strokeWidth={1.6} />
          </span>
          <span className="nav-label">Log out</span>
        </button>
      </div>
    </div>
  );
};
const Sidebar = ({ isOpen, onClose, menuRef }) => (
  <>
    <aside className="studio-sidebar">
      <SidebarContent onClose={onClose} />
    </aside>
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="left"
        className="mobile-sidebar"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          menuRef.current?.focus();
        }}
      >
        <SheetTitle className="studio-brand mb-6">
          <img className="brand-logo" src={`${process.env.PUBLIC_URL}/favicon.png`} alt="SMVEC logo" width="38" height="38" />
          <span>SMV Media</span>
        </SheetTitle>
        <SheetDescription className="sr-only">
          Navigate your media workspace
        </SheetDescription>
        <SidebarContent onClose={onClose} />
      </SheetContent>
    </Sheet>
  </>
);
export default Sidebar;
